import { useState, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Container, HStack } from '@chakra-ui/react';
import { Button, Text } from 'components/v4';
import AGGridMain from 'components/tables/AGGrid/AGGridMain';
import ActionMenu from 'components/presentational/ActionMenu';
import { MdEdit, MdDelete } from 'react-icons/md';
import useColumnState from 'hooks/useColumnState';
import { toast } from 'react-toastify';
import useGetCompanyServices from '@/queries/appAuth/useGetCompanyServices';
import { useQuery } from 'hooks/useCustomQuery';

import { getSites, deleteSite, getAddresses } from './api';
import { SITE_COLUMNS } from './consts';
import Create from './Create';
import Edit from './Edit';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import walkthroughIds from './walkthroughIds';

const EIGHTBYEIGHT_SERVICE_TYPE = 'EightByEight';

const SiteManagementIndex = () => {
    const gridRef = useRef();

    const [action, setAction] = useState('Get');
    const [selectedSite, setSelectedSite] = useState(null);
    const [siteToDelete, setSiteToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const { requiredScope } = useSelector((state) => state.login);
    const { currentCompany } = useSelector((state) => state.navigationLists);

    // Derive the 8x8 service ID from the current company's services list
    const { data: companyServices = [] } =
        useGetCompanyServices(currentCompany);
    const eightByEightService = companyServices.find(
        (s) => s.serviceType === EIGHTBYEIGHT_SERVICE_TYPE,
    );
    const serviceId = eightByEightService?.id;
    const platformId = eightByEightService?.platformID;

    const {
        data: sites = [],
        isLoading,
        refetch: refetchSites,
    } = useQuery({
        queryKey: ['8x8Sites', serviceId],
        queryFn: () => getSites(serviceId, requiredScope).then((r) => r.data),
        staleTime: 0,
        cacheTime: 0,
        enabled: !!serviceId,
        refetchOnWindowFocus: false,
    });

    const { data: addresses = [] } = useQuery({
        queryKey: ['8x8Addresses', currentCompany],
        queryFn: () =>
            getAddresses(currentCompany, requiredScope).then((r) => r.data),
        staleTime: 0,
        cacheTime: 0,
        enabled: !!currentCompany,
        refetchOnWindowFocus: false,
    });

    // Only offer addresses that have a valid 8x8 platform entry:
    //   - platformID matches the 8x8 service's platformID
    //   - status === 0 (Ok)
    //   - type must be PBX
    const filteredAddresses = useMemo(() => {
        if (!platformId) return [];
        return addresses.filter((address) => {
            const pa = address.platformAddresses?.find(
                (p) => p.platformID === platformId,
            );
            if (!pa) return false;
            if (pa.status !== 0) return false;
            return pa.type?.toLowerCase() === 'pbx';
        });
    }, [addresses, platformId]);

    const handleEdit = useCallback((rowData) => {
        setSelectedSite(rowData);
        setAction('Edit');
    }, []);

    const handleDeleteConfirm = async () => {
        if (!siteToDelete) return;
        setIsDeleting(true);
        try {
            await deleteSite(siteToDelete.id, requiredScope);
            setSiteToDelete(null);
            toast.success('Successfully deleted site.');
            refetchSites();
        } catch {
            toast.error('Failed to delete site. Please try again.');
        } finally {
            setIsDeleting(false);
        }
    };

    const baseColumns = useMemo(
        () => [
            ...SITE_COLUMNS,
            {
                maxWidth: 80,
                headerName: 'Actions',
                filter: false,
                suppressMovable: true,
                lockVisible: true,
                suppressColumnsToolPanel: true,
                suppressHeaderMenuButton: true,
                suppressHeaderFilterButton: true,
                enableRowGroup: false,
                resizable: false,
                sortable: false,
                lockPosition: 'right',
                cellStyle: () => ({
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }),
                cellRenderer: (params) => (
                    <ActionMenu
                        actionItems={[
                            {
                                label: 'Edit',
                                icon: MdEdit,
                                onClick: () => handleEdit(params.data),
                            },
                            {
                                label: 'Delete',
                                icon: MdDelete,
                                onClick: () => setSiteToDelete(params.data),
                            },
                        ]}
                    />
                ),
            },
        ],
        [handleEdit],
    );

    const { columns, onColumnVisible } = useColumnState(baseColumns);

    const tableData = useMemo(
        () => ({
            columns,
            data: sites,
        }),
        [columns, sites],
    );

    const pbxOptions = useMemo(() => {
        const uniquePbx = new Set();

        sites.forEach((site) => {
            if (site.pbx) uniquePbx.add(site.pbx);
        });

        return Array.from(uniquePbx).map((pbx) => ({
            value: pbx,
            label: pbx,
        }));
    }, [sites]);

    if (action === 'Create') {
        return (
            <Create
                prefill={selectedSite}
                serviceId={serviceId}
                companyId={currentCompany}
                platformId={platformId}
                requiredScope={requiredScope}
                addresses={filteredAddresses}
                pbxOptions={pbxOptions}
                onBack={() => {
                    setSelectedSite(null);
                    setAction('Get');
                }}
                onSuccess={() => {
                    setSelectedSite(null);
                    setAction('Get');
                    refetchSites();
                    toast.success('Successfully created site.');
                }}
            />
        );
    }

    if (action === 'Edit' && selectedSite) {
        return (
            <Edit
                site={selectedSite}
                requiredScope={requiredScope}
                addresses={filteredAddresses}
                pbxOptions={pbxOptions}
                onBack={() => {
                    setSelectedSite(null);
                    setAction('Get');
                }}
                onSuccess={() => {
                    setSelectedSite(null);
                    setAction('Get');
                    refetchSites();
                    toast.success('Successfully updated site.');
                }}
            />
        );
    }

    return (
        <>
            <Container
                as="div"
                maxW="container.xl"
                px={0}
                aria-label="site management table">
                <Text
                    sx={{
                        fontSize: '20',
                        fontWeight: '500',
                        marginBottom: '15px',
                    }}>
                    Manage Sites
                </Text>
                <Text color="gray.500" fontSize="sm" mb={4}>
                    Manage 8×8 sites for this service. Each site is linked to
                    one address and one PBX.
                </Text>
                <AGGridMain
                    gridRef={gridRef}
                    tableData={tableData}
                    isLoading={isLoading}
                    refetch={refetchSites}
                    onColumnVisible={onColumnVisible}
                    overlayNoRowsTemplate="No Sites Found"
                    titleName="Sites"
                    extraComponents={
                        <HStack pl={5} spacing={2}>
                            <Button
                                size="sm"
                                onClick={() => {
                                    setSelectedSite(null);
                                    setAction('Create');
                                }}
                                data-walkthroughid={
                                    walkthroughIds.ufAddSiteBtn
                                }>
                                + Add Site
                            </Button>
                        </HStack>
                    }
                />
            </Container>

            <DeleteConfirmationModal
                isOpen={!!siteToDelete}
                onClose={() => setSiteToDelete(null)}
                onConfirm={handleDeleteConfirm}
                siteName={siteToDelete?.name}
                isDeleting={isDeleting}
            />
        </>
    );
};

export default SiteManagementIndex;
